import type { PressEvent } from "@react-types/shared";
import { clsx } from "clsx";
import { forwardRef, useCallback, useState } from "react";
import { FocusScope, mergeProps, OverlayContainer, useDialog, usePreventScroll } from "react-aria";

import { useDOMRef } from "../../hooks/useDomRef.ts";
import type { AlertDialogProps, AlertPopoverProps, AllAlertDialogProps } from "../../types/dialogs.d.ts";
import { ActionButtonIntent } from "../ActionButton/settings.ts";
import { DialogButtonsWithErrorCallout } from "../Dialog/DialogButtonsWithErrorCallout.tsx";
import style from "../Dialog/TabDialog.module.css";
import type { HeaderBarProps } from "../HeaderBar/HeaderBar.tsx";

type DialogComponentProps = AllAlertDialogProps & {
  domTitleProps?: HeaderBarProps["domTitleProps"];
  placement?: string;
};

const AlertDialogComponent = forwardRef<HTMLDivElement, DialogComponentProps>(function AlertDialogComponent(
  props: DialogComponentProps,
  ref,
) {
  const dialogRef = useDOMRef(ref);
  const { dialogProps, titleProps } = useDialog({ role: "dialog", ...props }, dialogRef);

  const [onPressError, setOnPressError] = useState<string | null>(null);
  const handleOnPress = useCallback(
    (e: PressEvent) => {
      setOnPressError(null);
      return (props.mainButton?.onPress?.(e) as Promise<unknown> | undefined)?.catch?.((e: Error) => {
        setOnPressError(e.message);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.mainButton?.onPress],
  );

  return (
    <div
      ref={dialogRef}
      {...mergeProps(dialogProps, props.domProps)}
      className={clsx(
        style.dialog,
        style.alert_dialog,
        { [style.centered]: !!(props as AlertDialogProps).underlayProps },
        props.className,
      )}
      style={{ ...(props.domProps?.style || {}), width: props.width }}
      data-testid={"dialog"}
    >
      <div className={style.rounded_container}>
        {props.title != undefined && (
          <div className={style.alert_title} {...titleProps}>
            {props.title}
          </div>
        )}
        <div className={style.alert_content}>{props.children}</div>
        {props.mainButton != undefined && (
          <DialogButtonsWithErrorCallout
            {...props}
            mainButton={{ intent: ActionButtonIntent.Danger, ...props.mainButton, onPress: handleOnPress }}
            errorCallout={props.errorCallout ?? (onPressError ? { message: onPressError } : undefined)}
          />
        )}
      </div>
      {(props as AlertPopoverProps).arrowProps != undefined && (
        <div className={style.arrow} data-placement={props.placement} {...(props as AlertPopoverProps).arrowProps} />
      )}
    </div>
  );
});

const AlertDialog = forwardRef<HTMLDivElement, AllAlertDialogProps>(function AlertDialog(
  props: AllAlertDialogProps,
  ref,
) {
  usePreventScroll();

  return (
    <OverlayContainer
      {...((props as AlertDialogProps).underlayProps ?? {})}
      className={(props as AlertDialogProps).underlayProps ? clsx(style.overlay, style.overlay_center) : undefined}
    >
      <FocusScope contain restoreFocus autoFocus>
        <AlertDialogComponent {...props} domProps={props.domProps} ref={ref} />
      </FocusScope>
    </OverlayContainer>
  );
});

export { AlertDialog, AlertDialogComponent };
